import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { auth, ApiClientError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Alert } from '../components/ui';

export function Register() {
  const { account, login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', company: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (account) return <Navigate to="/" replace />;

  function update(field: keyof typeof form) {
    return (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((current) => ({ ...current, [field]: event.target.value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await auth.register({
        email: form.email,
        password: form.password,
        name: form.name || undefined,
        company: form.company || undefined,
      });

      // Registration doesn't create a session, so sign in with the same
      // credentials and drop the user straight into the dashboard.
      await login(form.email, form.password);
      navigate('/');
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not create the account');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit}>
        <h1>Create an account</h1>
        <p className="subtitle">Start building against the Difference API.</p>

        {error && <Alert>{error}</Alert>}

        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={update('name')} autoComplete="name" />
        </label>

        <label className="field">
          <span>Company</span>
          <input value={form.company} onChange={update('company')} autoComplete="organization" />
        </label>

        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={form.email}
            onChange={update('email')}
            autoComplete="email"
            required
          />
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={form.password}
            onChange={update('password')}
            autoComplete="new-password"
            minLength={12}
            required
          />
          <span className="muted" style={{ fontSize: 12.5 }}>
            At least 12 characters.
          </span>
        </label>

        <button className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create account'}
        </button>

        <p className="muted" style={{ marginTop: 16, marginBottom: 0 }}>
          Already registered? <Link to="/login">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
